import * as Y from "yjs";

import { useBattlemapStore } from "@/stores/useBattlemapStore";

interface FogPropertiesPanelProps {
  activeTool: string;
  doc?: Y.Doc;
}

export function FogPropertiesPanel({ activeTool }: FogPropertiesPanelProps) {
  const {
    fogMode,
    setFogMode,
    fogTool,
    brushSize,
    setBrushSize,
    settings,
    syncSettings,
  } = useBattlemapStore();

  if (activeTool !== "fog") return null;

  return (
    <div className="h-full flex flex-col bg-slate-800 border-l border-slate-700">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-200">Fog of War</h3>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Fog Tools */}
        {/* Fog Tools removed - moved to toolbar */}

        {/* Mode Toggle */}
        <div className="space-y-2" role="group" aria-label="Mode">
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Mode
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setFogMode("hide")}
              className={`flex-1 px-3 py-2 text-sm rounded transition-colors border ${
                fogMode === "hide"
                  ? "bg-slate-700 text-white border-slate-600 ring-1 ring-slate-500"
                  : "bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800"
              }`}
            >
              Hide
            </button>
            <button
              onClick={() => setFogMode("reveal")}
              className={`flex-1 px-3 py-2 text-sm rounded transition-colors border ${
                fogMode === "reveal"
                  ? "bg-sky-600 text-white border-sky-500 ring-1 ring-sky-400"
                  : "bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800"
              }`}
            >
              Reveal
            </button>
          </div>
          <p className="text-xs text-slate-500">
            {fogMode === "hide"
              ? "Add fog to obscure areas"
              : "Remove fog to reveal areas"}
          </p>
        </div>

        {/* Global Fog Opacity */}
        <div
          className="space-y-2 pb-4 border-b border-slate-700"
          role="group"
          aria-label="Global Fog Opacity"
        >
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Global Fog Opacity (
            {Math.round((settings.fogOpacity ?? 0.85) * 100)}%)
          </label>
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.05"
            value={settings.fogOpacity ?? 0.85}
            onChange={(e) =>
              syncSettings({ ...settings, fogOpacity: Number(e.target.value) })
            }
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-sky-500"
          />
          <p className="text-[10px] text-slate-500 italic">
            Note: This affects the visual opacity of the entire Fog of War layer
            for all players.
          </p>
        </div>

        {/* Brush Size (for brush tool only) */}
        {fogTool === "brush" && (
          <div className="space-y-2" role="group" aria-label="Brush Size">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Brush Size ({brushSize}px)
            </label>
            <input
              type="range"
              min="10"
              max="200"
              step="10"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-sky-500"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>10px</span>
              <span>200px</span>
            </div>
          </div>
        )}

        {/* Fill Tool Info (for fill tool only) */}
        {fogTool === "fill" && (
          <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <h4 className="text-xs font-semibold text-slate-300 mb-2">
              Fill Behavior
            </h4>
            <div className="space-y-1.5 text-xs text-slate-400">
              <div className="flex items-start gap-2">
                <span className="text-sky-400 font-bold">•</span>
                <p>
                  <span className="text-slate-300 font-medium">Default:</span>{" "}
                  Fill is bounded by walls
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-sky-400 font-bold">•</span>
                <p>
                  <span className="text-slate-300 font-medium">
                    Alt + Click:
                  </span>{" "}
                  Unbounded fill
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Polygon Tool Info */}
        {fogTool === "polygon" && (
          <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <h4 className="text-xs font-semibold text-slate-300 mb-2">
              Polygon Tool
            </h4>
            <div className="space-y-1.5 text-xs text-slate-400">
              <div className="flex items-start gap-2">
                <span className="text-sky-400 font-bold">•</span>
                <p>Click to add points</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-sky-400 font-bold">•</span>
                <p>Double-click to finish</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-sky-400 font-bold">•</span>
                <p>Esc to cancel</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
